/**
 * Tipos del módulo de tap-to-lookup de palabras, compartido entre la
 * conversación (coach) y el karaoke del player (Fase 19.F).
 *
 * `WordLookupEntry` es el SUPERSET de lo que puede mostrar el popover:
 *  - La conversación lo alimenta con el `LookupResult` del `/lookup`
 *    (translation/definition/pos/examples) — los campos ricos quedan `undefined`.
 *  - El player lo alimenta con la entrada del léxico de la Biblioteca, que SÍ
 *    trae `ipa`, `cefr` y `senses` (los mismos datos que muestra la Biblioteca).
 *
 * El popover renderiza condicionalmente: lo que viene se muestra, lo que falta
 * no — así un mismo componente sirve a ambos sin ramas por pantalla.
 */
import type { ContextMeaningDraft, ContextMeaningMissingNotice } from '@orborix/shared-types';
import type { LexiconWord } from '../../lib/lexiconApi.js';

export interface WordLookupEntry {
  translation: string;
  definition: string | null;
  partOfSpeech: string | null;
  examples: ReadonlyArray<string>;
  /** Sólo del léxico (Biblioteca). */
  ipa?: string | null;
  cefr?: string | null;
  senses?: ReadonlyArray<string>;
  /**
   * Procedencia de la ficha, cuando el resolver la sabe: `lexicon` = catálogo
   * de inglés curado, `dictionary` = generada on-demand por LLM (puede ser
   * español o un typo — el LLM traduce lo que le pasen). Sólo la trae
   * `fetchGlossaryBatch`; el resolver por tap de la conversación no la setea.
   */
  source?: 'lexicon' | 'dictionary';
  /**
   * El «significado de esa frase» (encargo 49), cuando está prendido: qué
   * significado del diccionario corresponde a la palabra en esa oración, o por
   * qué no se eligió. Con esto el popover dice de dónde sale lo que muestra
   * (del diccionario, o traducción automática rotulada) y ofrece abrir la ficha.
   */
  meaning?: MeaningLookup;
}

/** Lo que el popover necesita saber del significado de la palabra en esa oración. */
export type MeaningLookup =
  | { kind: 'picked'; entry: LexiconWord; meaningId: string }
  | {
      kind: 'unchosen';
      reason: 'none' | 'ambiguous' | 'failed' | 'limit';
      entry: LexiconWord;
      missing: ContextMeaningMissingNotice | null;
    }
  /** No está en el diccionario (o es una ficha heredada sin significados). */
  | {
      kind: 'missing-word';
      word: string;
      missing: ContextMeaningMissingNotice;
      autoTranslation: boolean;
    }
  | { kind: 'not-english'; word: string }
  /** Una palabra nueva aprobada por una persona, todavía fuera del diccionario. */
  | { kind: 'provisional'; word: string; draft: ContextMeaningDraft };

/**
 * Resolver de palabra inyectable. La conversación resuelve contra `/lookup`
 * (puede pegar al LLM); el player resuelve contra el glosario local del
 * capítulo (pre-fetcheado, cero red por tap).
 */
export interface WordResolver {
  /**
   * Resultado cacheado/local instantáneo (sin red) o `undefined`. `at` es el
   * índice de la palabra en la línea (separada por espacios): el «significado
   * de esa frase» lo necesita para ubicarla; una frase confirmada no lo trae.
   */
  peekWord: (word: string, context: string, at?: number) => WordLookupEntry | undefined;
  /** Resolución async (cache-hit, fetch, o lectura del mapa local). */
  resolveWord: (word: string, context: string, at?: number) => Promise<WordLookupEntry>;
  /**
   * Respuesta PROVISORIA instantánea para los casos en que `peekWord` no puede
   * responder pero igual hay algo local decente que mostrar — hoy: la palabra
   * polisémica del karaoke, cuyo sentido definitivo se re-resuelve contra
   * `/lookup` con la oración (~2 s).
   *
   * Existe para que el popover NO sea un spinner de 2 s esperando el matiz: se
   * abre con la entrada del léxico (traducción + ipa + cefr + acepciones) y la
   * traducción se refina sola cuando llega la contextual. Opcional: la
   * conversación no la implementa (sin glosario local no hay nada provisorio).
   */
  peekProvisional?: (word: string, context: string) => WordLookupEntry | undefined;
}

/** Normaliza una palabra cruda a su forma de búsqueda (matchea `text_norm`).
 *  Lowercase + recorte de puntuación en los bordes; conserva apóstrofos y
 *  guiones internos ("don't", "self-control"). */
export function normalizeWord(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/^[^a-zà-ÿ]+/i, '')
    .replace(/[^a-zà-ÿ']+$/i, '');
}
